import React, { useEffect, useState } from "react";
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Line } from "react-chartjs-2";
import { API_URL } from "../components/config";
ChartJS.register(
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Title,
  Tooltip,
  Legend
);

const Hourly = () => {
  const [data, setData] = useState([]);
  const [scrollPosition, setScrollPosition] = useState(0);
  const pointsToShow = 12; // Show 6 hours (12 half-hour points)

  useEffect(() => {
    const fetchData = async () => {
      const response = await fetch(`${API_URL}/stats_half_hourly`);
      const result = await response.json();
      setData(result.slice(-24));
    };
    fetchData();

    // Set up auto-refresh every 5 minutes
    const interval = setInterval(fetchData, 300000);
    return () => clearInterval(interval);
  }, []);

  const getVisibleData = (fullData) => {
    return fullData.slice(scrollPosition, scrollPosition + pointsToShow);
  };

  const handleScroll = (direction) => {
    if (direction === "left" && scrollPosition > 0) {
      setScrollPosition(scrollPosition - 1);
    } else if (
      direction === "right" &&
      scrollPosition < data.length - pointsToShow
    ) {
      setScrollPosition(scrollPosition + 1);
    }
  };

  const createLineChartData = (
    fullLabels,
    fullData,
    label,
    color = "rgba(75, 192, 192, 1)"
  ) => {
    const visibleLabels = getVisibleData(fullLabels);
    const visibleDataPoints = getVisibleData(fullData);

    return {
      labels: visibleLabels,
      datasets: [
        {
          label,
          data: visibleDataPoints,
          borderColor: color,
          backgroundColor: color.replace("1)", "0.1)"),
          borderWidth: 2,
          fill: true,
          tension: 0.4,
          pointRadius: 4,
          pointHoverRadius: 6,
          pointBackgroundColor: color,
        },
      ],
    };
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top",
        labels: {
          font: {
            size: 14,
            weight: "bold",
          },
          padding: 20,
        },
      },
      tooltip: {
        mode: "index",
        intersect: false,
        backgroundColor: "rgba(255, 255, 255, 0.9)",
        titleColor: "#000",
        bodyColor: "#000",
        borderColor: "#ddd",
        borderWidth: 1,
        padding: 10,
        bodyFont: {
          size: 14,
        },
        titleFont: {
          size: 16,
          weight: "bold",
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: "rgba(0, 0, 0, 0.1)",
        },
        ticks: {
          font: {
            size: 12,
          },
        },
      },
      x: {
        grid: {
          color: "rgba(0, 0, 0, 0.1)",
        },
        ticks: {
          font: {
            size: 12,
          },
          maxRotation: 45,
          minRotation: 45,
        },
      },
    },
  };

  const labels = data.map((item) =>
    new Date(item.datefor).toLocaleTimeString()
  );

  const ChartContainer = ({ title, chartData, color }) => (
    <div className="mb-8 bg-white rounded-lg shadow-lg p-4">
      <h2 className="text-2xl font-bold mb-4 text-gray-800">{title}</h2>
      <div className="relative h-[400px]">
        <div className="absolute inset-0">
          <Line data={chartData} options={chartOptions} />
        </div>
        <div className="absolute top-1/2 left-2 transform -translate-y-1/2">
          <button
            onClick={() => handleScroll("left")}
            className="bg-gray-200 hover:bg-gray-300 rounded-full p-2 disabled:opacity-50"
            disabled={scrollPosition === 0}
          >
            ←
          </button>
        </div>
        <div className="absolute top-1/2 right-2 transform -translate-y-1/2">
          <button
            onClick={() => handleScroll("right")}
            className="bg-gray-200 hover:bg-gray-300 rounded-full p-2 disabled:opacity-50"
            disabled={scrollPosition >= data.length - pointsToShow}
          >
            →
          </button>
        </div>
      </div>
    </div>
  );

  const chartColors = {
    blue: "rgba(53, 162, 235, 1)",
    green: "rgba(75, 192, 192, 1)",
    purple: "rgba(153, 102, 255, 1)",
    orange: "rgba(255, 159, 64, 1)",
    red: "rgba(255, 99, 132, 1)",
    yellow: "rgba(255, 205, 86, 1)",
    pink: "rgba(255, 99, 140, 1)",
    teal: "rgba(54, 162, 235, 1)",
    navy: "rgba(0, 0, 128, 1)",
    maroon: "rgba(128, 0, 0, 1)",
    olive: "rgba(128, 128, 0, 1)",
    lime: "rgba(0, 255, 0, 1)",
    coral: "rgba(255, 127, 80, 1)",
    gold: "rgba(255, 215, 0, 1)",
    lavender: "rgba(230, 230, 250, 1)",
  };

  return (
    <div className="max-w-6xl mx-auto p-6 bg-gray-50">
      <h1 className="text-4xl font-bold mb-8 text-gray-900 text-center">
        Application Statistics
      </h1>

      <div className="grid grid-cols-1 md:grid-cols-1 gap-6">
        <ChartContainer
          title="KMs Billed"
          chartData={createLineChartData(
            labels,
            data.map((item) => item.kms_billed),
            "KMs Billed",
            chartColors.blue
          )}
        />

        <ChartContainer
          title="Trips Count"
          chartData={createLineChartData(
            labels,
            data.map((item) => item.trips_count),
            "Trips Count",
            chartColors.green
          )}
        />

        {/* Add all other charts following the same pattern */}
        {/* Example for a few more charts */}
        <ChartContainer
          title="Customers Count"
          chartData={createLineChartData(
            labels,
            data.map((item) => item.customers_count),
            "Customers Count",
            chartColors.purple
          )}
        />

        <ChartContainer
          title="Drivers Count"
          chartData={createLineChartData(
            labels,
            data.map((item) => item.drivers_count),
            "Drivers Count",
            chartColors.orange
          )}
        />
        <ChartContainer
          title="Trips per Customer"
          chartData={createLineChartData(
            labels,
            data.map((item) => item.trips_per_customer),
            "Trips Per Customer",
            chartColors.orange
          )}
        />
        <ChartContainer
          title="Billed Amount USD"
          chartData={createLineChartData(
            labels,
            data.map((item) => item.billed_amount_usd),
            "Billed Amount USD",
            chartColors.orange
          )}
        />
        <ChartContainer
          title="Average Trip Rate"
          chartData={createLineChartData(
            labels,
            data.map((item) => item.average_trip_rate_us),
            "Average Trip Rate",
            chartColors.orange
          )}
        />
        <ChartContainer
          title="Mode Rate Usd"
          chartData={createLineChartData(
            labels,
            data.map((item) => item.mode_rate_usd),
            "Drivers Count",
            chartColors.orange
          )}
        />
        <ChartContainer
          title="Average Billed"
          chartData={createLineChartData(
            labels,
            data.map((item) => item.average_billed),
            "Average Billed",
            chartColors.orange
          )}
        />
        <ChartContainer
          title="New Customer "
          chartData={createLineChartData(
            labels,
            data.map((item) => item.new_customer_count),
            "New Customer ",
            chartColors.orange
          )}
        />
        <ChartContainer
          title="New Drivers"
          chartData={createLineChartData(
            labels,
            data.map((item) => item.new_drivers_count),
            "New Drivers",
            chartColors.lime
          )}
        />
        <ChartContainer
          title="complaints_count"
          chartData={createLineChartData(
            labels,
            data.map((item) => item.complaints_count),
            "complaints_count",
            chartColors.green
          )}
        />
        <ChartContainer
          title="Total Rating"
          chartData={createLineChartData(
            labels,
            data.map((item) => item.total_rating_count),
            "total_rating_count",
            chartColors.orange
          )}
        />
        <ChartContainer
          title="Average Rating"
          chartData={createLineChartData(
            labels,
            data.map((item) => item.ave_rating_count),
            "Average Rating",
            chartColors.orange
          )}
        />

        <ChartContainer
          title="Drivers Open Balance"
          chartData={createLineChartData(
            labels,
            data.map((item) => item.driver_open_balance),
            "Drivers Open Balance",
            chartColors.orange
          )}
        />
        <ChartContainer
          title="Drivers Top Up"
          chartData={createLineChartData(
            labels,
            data.map((item) => item.driver_top_up),
            "Drivers Top Up",
            chartColors.orange
          )}
        />
        <ChartContainer
          title="Drivers Billed Charges"
          chartData={createLineChartData(
            labels,
            data.map((item) => item.driver_billed_charges),
            "Drivers Billed Charges",
            chartColors.orange
          )}
        />
        <ChartContainer
          title="Drivers Withdrawals"
          chartData={createLineChartData(
            labels,
            data.map((item) => item.driver_withdrawals),
            "Drivers Withdrawals",
            chartColors.orange
          )}
        />
        <ChartContainer
          title="Drivers Escrow"
          chartData={createLineChartData(
            labels,
            data.map((item) => item.driver_escrow),
            "Drivers Escrow",
            chartColors.orange
          )}
        />
        <ChartContainer
          title="Drivers Promo"
          chartData={createLineChartData(
            labels,
            data.map((item) => item.driver_promo),
            "Drivers Promo",
            chartColors.orange
          )}
        />
        <ChartContainer
          title="Drivers Deductions"
          chartData={createLineChartData(
            labels,
            data.map((item) => item.driver_deductions),
            "Drivers Deductions",
            chartColors.orange
          )}
        />

        <ChartContainer
          title="Drivers Close Balance"
          chartData={createLineChartData(
            labels,
            data.map((item) => item.driver_close_balance),
            "Drivers Close Balance",
            chartColors.orange
          )}
        />

        <ChartContainer
          title="Customer Open Balance"
          chartData={createLineChartData(
            labels,
            data.map((item) => item.customer_open_balance),
            "Customer Open Balance",
            chartColors.orange
          )}
        />

        <ChartContainer
          title="Customer Top Up"
          chartData={createLineChartData(
            labels,
            data.map((item) => item.customer_to_up),
            "Customer Top Up",
            chartColors.orange
          )}
        />

        <ChartContainer
          title="Drivers Count"
          chartData={createLineChartData(
            labels,
            data.map((item) => item.customer_billed_charges),
            "Drivers Count",
            chartColors.orange
          )}
        />

        <ChartContainer
          title="Customer Withdrawals"
          chartData={createLineChartData(
            labels,
            data.map((item) => item.customer_withdrawals),
            "Customers Withdrawals",
            chartColors.orange
          )}
        />
        <ChartContainer
          title="Customer Escrow"
          chartData={createLineChartData(
            labels,
            data.map((item) => item.customer_escrow),
            "Customer Escrow",
            chartColors.orange
          )}
        />
        <ChartContainer
          title="Drivers Count"
          chartData={createLineChartData(
            labels,
            data.map((item) => item.customer_promo),
            "Drivers Count",
            chartColors.orange
          )}
        />
        <ChartContainer
          title="Customer Deduction"
          chartData={createLineChartData(
            labels,
            data.map((item) => item.customer_deduction),
            "Customer Deduction",
            chartColors.orange
          )}
        />
        <ChartContainer
          title="Drivers Count"
          chartData={createLineChartData(
            labels,
            data.map((item) => item.customer_additions),
            "Customer additions",
            chartColors.orange
          )}
        />
        <ChartContainer
          title="Customer Close Balance"
          chartData={createLineChartData(
            labels,
            data.map((item) => item.customer_close_balance),
            "Customer Close Balance",
            chartColors.orange
          )}
        />
        <ChartContainer
          title="Company Open Balance"
          chartData={createLineChartData(
            labels,
            data.map((item) => item.company_open_balance),
            "Company Open Balance",
            chartColors.orange
          )}
        />
        <ChartContainer
          title="Drivers Count"
          chartData={createLineChartData(
            labels,
            data.map((item) => item.company_income_from_charges),
            "Drivers Count",
            chartColors.orange
          )}
        />
        <ChartContainer
          title="Drivers Count"
          chartData={createLineChartData(
            labels,
            data.map((item) => item.company_promotions),
            "Drivers Count",
            chartColors.orange
          )}
        />
        <ChartContainer
          title="Drivers Count"
          chartData={createLineChartData(
            labels,
            data.map((item) => item.company_withdraws_out),
            "Drivers Count",
            chartColors.orange
          )}
        />
        <ChartContainer
          title="Drivers Count"
          chartData={createLineChartData(
            labels,
            data.map((item) => item.company_close_balance),
            "Drivers Count",
            chartColors.orange
          )}
        />
        <ChartContainer
          title="Drivers Count"
          chartData={createLineChartData(
            labels,
            data.map((item) => item.sum_commission_uw),
            "Drivers Count",
            chartColors.orange
          )}
        />
        <ChartContainer
          title="Drivers Count"
          chartData={createLineChartData(
            labels,
            data.map((item) => item.sum_commission_uw),
            "Drivers Count",
            chartColors.orange
          )}
        />
        <ChartContainer
          title="Drivers Count"
          chartData={createLineChartData(
            labels,
            data.map((item) => item.sum_promotion_uw),
            "Drivers Count",
            chartColors.orange
          )}
        />
        <ChartContainer
          title="Drivers Count"
          chartData={createLineChartData(
            labels,
            data.map((item) => item.sum_promotion_vw),
            "Drivers Count",
            chartColors.orange
          )}
        />
        <ChartContainer
          title="Drivers Count"
          chartData={createLineChartData(
            labels,
            data.map((item) => item.sum_top_up_uw),
            "Drivers Count",
            chartColors.orange
          )}
        />
        <ChartContainer
          title="Drivers Count"
          chartData={createLineChartData(
            labels,
            data.map((item) => item.sum_top_up_vw),
            "Drivers Count",
            chartColors.orange
          )}
        />
        <ChartContainer
          title="Drivers Count"
          chartData={createLineChartData(
            labels,
            data.map((item) => item.sum_withdrawal_uw),
            "Drivers Count",
            chartColors.purple
          )}
        />
        <ChartContainer
          title="Drivers Count"
          chartData={createLineChartData(
            labels,
            data.map((item) => item.sum_mischief_penalty_uw),
            "Drivers Count",
            chartColors.orange
          )}
        />
        <ChartContainer
          title="Drivers Count"
          chartData={createLineChartData(
            labels,
            data.map((item) => item.sum_mischief_penalty_vw),
            "Drivers Count",
            chartColors.gold
          )}
        />
        <ChartContainer
          title="Drivers Count"
          chartData={createLineChartData(
            labels,
            data.map((item) => item.user_wallet_total_balance),
            "Drivers Count",
            chartColors.pink
          )}
        />
        <ChartContainer
          title="Drivers Count"
          chartData={createLineChartData(
            labels,
            data.map((item) => item.main_wallet_total_balance),
            "Drivers Count",
            chartColors.orange
          )}
        />
        <ChartContainer
          title="Drivers Count"
          chartData={createLineChartData(
            labels,
            data.map((item) => item.main_wallet_balance),
            "Drivers Count",
            chartColors.lavender
          )}
        />
        <ChartContainer
          title="Drivers Count"
          chartData={createLineChartData(
            labels,
            data.map((item) => item.revenue_wallet_total_balance),
            "Drivers Count",
            chartColors.orange
          )}
        />
        <ChartContainer
          title="Drivers Count"
          chartData={createLineChartData(
            labels,
            data.map((item) => item.vendor_wallet_total_balance),
            "Drivers Count",
            chartColors.olive
          )}
        />
        <ChartContainer
          title="Drivers Count"
          chartData={createLineChartData(
            labels,
            data.map((item) => item.escrow_total_balance),
            "Drivers Count",
            chartColors.coral
          )}
        />
        <ChartContainer
          title="Drivers Count"
          chartData={createLineChartData(
            labels,
            data.map((item) => item.signed_up_today),
            "Drivers Count",
            chartColors.blue
          )}
        />
        <ChartContainer
          title="Users Logged in today"
          chartData={createLineChartData(
            labels,
            data.map((item) => item.users_logged_in_today),
            "login count 1",
            chartColors.purple
          )}
        />
        <ChartContainer
          title="Users Logged in two days"
          chartData={createLineChartData(
            labels,
            data.map((item) => item.users_logged_in_two_days_ago),
            "Users Logged in two days",
            chartColors.orange
          )}
        />
        <ChartContainer
          title="Users Logged in three days"
          chartData={createLineChartData(
            labels,
            data.map((item) => item.users_logged_in_three_days_ago),
            "Users Logged in three days",
            chartColors.orange
          )}
        />
        <ChartContainer
          title="Users Logged in seven days"
          chartData={createLineChartData(
            labels,
            data.map((item) => item.users_logged_in_seven_days_ago),
            "Users Logged in seven days",
            chartColors.orange
          )}
        />
        <ChartContainer
          title="Users Logged in fourteen days ago"
          chartData={createLineChartData(
            labels,
            data.map((item) => item.users_logged_in_fourteen_days_ago),
            "Users Logged in fourteen days ago",
            chartColors.orange
          )}
        />
        <ChartContainer
          title="Users Logged in thirty days ago"
          chartData={createLineChartData(
            labels,
            data.map((item) => item.users_logged_in_thirty_days_ago),
            "Users Logged in thirty days ago",
            chartColors.orange
          )}
        />
        <ChartContainer
          title="Average Engagement Time"
          chartData={createLineChartData(
            labels,
            data.map((item) => item.average_engagement_time),
            "Average Engagement Time",
            chartColors.orange
          )}
        />
        {/* Continue adding all other charts in the same way */}
      </div>
    </div>
  );
};

export default Hourly;
